import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import { logoBanner } from "../Common/BrandLogo";
import ReportButton from "../Common/ReportButton";
import FarmAvatar from "../Common/FarmAvatar";
import { useNavigate } from "react-router-dom";
import { getProfilePath } from "../../utils/storefront";
import { buildCommentThreads } from "../../utils/communityComments";

const formatCommentDate = (timestamp) => {
  if (!timestamp?.seconds) return "Just now";

  return new Date(timestamp.seconds * 1000).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
};

const CommunityComments = ({ post, currentUser, userProfile, onCountChange }) => {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [replyingToCommentId, setReplyingToCommentId] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [commentIdentity, setCommentIdentity] = useState("profile");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const isAdmin = userProfile?.isAdmin === true || userProfile?.admin === true;
  const adminDisplayName =
    userProfile?.name ||
    currentUser?.displayName ||
    currentUser?.email ||
    "Community member";
  const { topLevelComments, repliesByParentId } = useMemo(
    () => buildCommentThreads(comments),
    [comments]
  );

  useEffect(() => {
    if (!post?.id) return;

    const commentsQuery = query(
      collection(db, "communityPosts", post.id, "comments"),
      where("status", "==", "active"),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      commentsQuery,
      (snapshot) => {
        const nextComments = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setComments(nextComments);
        onCountChange?.(nextComments.length);
        setLoading(false);
      },
      (error) => {
        console.error("Community comments listener failed:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [onCountChange, post?.id]);

  const handleReply = async (event, parentCommentId = "") => {
    event.preventDefault();

    if (!currentUser?.uid) {
      alert("Please sign in to reply.");
      return;
    }

    if (!requireVerifiedEmail(currentUser, userProfile)) return;

    const isNestedReply = Boolean(parentCommentId);
    const cleanBody = (isNestedReply ? replyBody : body).trim();
    if (!cleanBody) return;

    try {
      setSubmitting(true);
      const useOfficialIdentity = isAdmin && commentIdentity === "official";
      const commentPayload = {
        body: cleanBody,
        authorId: currentUser.uid,
        authorName: useOfficialIdentity
          ? "Official MyFarmingPal"
          : adminDisplayName,
        authorPhoto: useOfficialIdentity
          ? ""
          : userProfile?.profilePicture || currentUser.photoURL || "",
        authorFarmAnimal: useOfficialIdentity ? "" : userProfile?.farmAnimal || "",
        authorFarmAnimalSelectionExplicit:
          !useOfficialIdentity &&
          userProfile?.farmAnimalSelectionExplicit === true,
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (parentCommentId) {
        commentPayload.parentCommentId = parentCommentId;
      }

      if (useOfficialIdentity) {
        commentPayload.commentAsOfficial = true;
        commentPayload.officialIdentity = "myfarmingpal";
        commentPayload.createdByAdminId = currentUser.uid;
      }

      await addDoc(collection(db, "communityPosts", post.id, "comments"), commentPayload);

      if (isNestedReply) {
        setReplyBody("");
        setReplyingToCommentId("");
      } else {
        setBody("");
      }
    } catch (error) {
      console.error("Failed adding community comment:", error);
      alert("Could not add reply. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startEditingComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditBody(comment.body || "");
  };

  const handleSaveCommentEdit = async (event) => {
    event.preventDefault();
    if (!editingCommentId) return;

    const cleanEditBody = editBody.trim();
    if (!cleanEditBody) return;

    try {
      setSavingEdit(true);
      await updateDoc(
        doc(db, "communityPosts", post.id, "comments", editingCommentId),
        {
          body: cleanEditBody,
          updatedAt: serverTimestamp(),
        }
      );
      setEditingCommentId(null);
      setEditBody("");
    } catch (error) {
      console.error("Failed editing community comment:", error);
      alert("Could not save reply. Please try again.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRemoveComment = async (comment) => {
    const canRemove = comment.authorId === currentUser?.uid || isAdmin;
    if (!canRemove) return;
    if (!window.confirm("Remove this reply?")) return;

    try {
      await updateDoc(
        doc(db, "communityPosts", post.id, "comments", comment.id),
        {
          status: "archived",
          updatedAt: serverTimestamp(),
        }
      );
    } catch (error) {
      console.error("Failed removing community comment:", error);
      alert("Could not remove reply. Please try again.");
    }
  };

  return (
    <div className="community-comments">
      <form className="community-comment-form" onSubmit={handleReply}>
        {isAdmin && (
          <label className="community-comment-identity">
            <span>Comment as</span>
            <select
              value={commentIdentity}
              onChange={(event) => setCommentIdentity(event.target.value)}
            >
              <option value="profile">{adminDisplayName}</option>
              <option value="official">Official MyFarmingPal</option>
            </select>
          </label>
        )}
        <input
          type="text"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Write a helpful reply..."
          maxLength={800}
        />
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Replying..." : "Reply"}
        </button>
      </form>

      {loading ? (
        <p className="community-comments-empty">Loading replies...</p>
      ) : comments.length === 0 ? (
        <p className="community-comments-empty">No replies yet. Be helpful and start the thread.</p>
      ) : (
        <div className="community-comment-list">
          {topLevelComments.map((comment) => {
            const canRemove = comment.authorId === currentUser?.uid || isAdmin;
            const canEdit = comment.authorId === currentUser?.uid || isAdmin;
            const isOfficialComment =
              comment.commentAsOfficial === true &&
              comment.officialIdentity === "myfarmingpal";
            const displayName = isOfficialComment
              ? "Official MyFarmingPal"
              : comment.authorName || "Community member";
            const avatarAlt = isOfficialComment ? "MyFarmingPal logo" : displayName;
            const isEditing = editingCommentId === comment.id;
            const authorPath = comment.authorId
              ? getProfilePath({ id: comment.authorId, uid: comment.authorId, name: displayName })
              : "";
            const canOpenAuthorProfile = Boolean(!isOfficialComment && comment.authorId);
            const childReplies = repliesByParentId.get(comment.id) || [];

            return (
              <div className="community-comment-thread" key={comment.id}>
                <div
                  className={`community-comment${isOfficialComment ? " is-official-comment" : ""}`}
                >
                  <button
                    type="button"
                    className="community-comment-avatar community-author-link"
                    onClick={() => canOpenAuthorProfile && navigate(authorPath)}
                    disabled={!canOpenAuthorProfile}
                    aria-label={`View ${displayName}'s profile`}
                  >
                    {isOfficialComment ? (
                      <img src={logoBanner} alt={avatarAlt} />
                    ) : (
                      <FarmAvatar
                        profile={{
                          id: comment.authorId,
                          uid: comment.authorId,
                          name: displayName,
                          photo: comment.authorPhoto,
                          farmAnimal: comment.authorFarmAnimal,
                          farmAnimalSelectionExplicit:
                            comment.authorFarmAnimalSelectionExplicit === true,
                        }}
                        className="community-comment-avatar-inner"
                      />
                    )}
                  </button>

                  <div className="community-comment-body">
                    <div className="community-comment-meta">
                      <button
                        type="button"
                        className="community-author-name-link"
                        onClick={() => canOpenAuthorProfile && navigate(authorPath)}
                        disabled={!canOpenAuthorProfile}
                      >
                        {displayName}
                      </button>
                      {isOfficialComment && (
                        <span className="community-official-badge">Official</span>
                      )}
                      <span>{formatCommentDate(comment.createdAt)}</span>
                    </div>
                    {isEditing ? (
                      <form className="community-comment-edit-form" onSubmit={handleSaveCommentEdit}>
                        <input
                          type="text"
                          value={editBody}
                          onChange={(event) => setEditBody(event.target.value)}
                          maxLength={800}
                        />
                        <div className="community-comment-actions">
                          <button type="submit" className="btn-primary" disabled={savingEdit}>
                            {savingEdit ? "Saving..." : "Save"}
                          </button>
                          <button
                            type="button"
                            className="btn-soft"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditBody("");
                            }}
                            disabled={savingEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <p>{comment.body}</p>
                    )}
                    <div className="community-comment-actions">
                      <button
                        type="button"
                        className="btn-soft"
                        onClick={() =>
                          setReplyingToCommentId((current) =>
                            current === comment.id ? "" : comment.id
                          )
                        }
                      >
                        Reply
                      </button>
                      <ReportButton
                        type="community_comment"
                        targetId={comment.id}
                        targetOwnerId={comment.authorId}
                        targetTitle={`Reply on ${post.title || "community post"}`}
                        extraData={{
                          parentPostId: post.id,
                          parentPostTitle: post.title || "",
                          targetCommentText: comment.body || "",
                        }}
                        compact
                      />
                      {canEdit && !isEditing && (
                        <button
                          type="button"
                          className="btn-soft"
                          onClick={() => startEditingComment(comment)}
                        >
                          Edit
                        </button>
                      )}
                      {canRemove && (
                        <button
                          type="button"
                          className="btn-danger-soft"
                          onClick={() => handleRemoveComment(comment)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {replyingToCommentId === comment.id && (
                      <form
                        className="community-comment-form community-reply-form"
                        onSubmit={(event) => handleReply(event, comment.id)}
                      >
                        <input
                          type="text"
                          value={replyBody}
                          onChange={(event) => setReplyBody(event.target.value)}
                          placeholder={`Reply to ${displayName}...`}
                          maxLength={800}
                        />
                        <button type="submit" className="btn-primary" disabled={submitting}>
                          {submitting ? "Replying..." : "Reply"}
                        </button>
                      </form>
                    )}
                  </div>
                </div>

                {childReplies.length > 0 && (
                  <div className="community-reply-list">
                    {childReplies.map((reply) => {
                      const replyCanRemove = reply.authorId === currentUser?.uid || isAdmin;
                      const replyCanEdit = reply.authorId === currentUser?.uid || isAdmin;
                      const replyIsOfficial =
                        reply.commentAsOfficial === true &&
                        reply.officialIdentity === "myfarmingpal";
                      const replyDisplayName = replyIsOfficial
                        ? "Official MyFarmingPal"
                        : reply.authorName || "Community member";
                      const replyIsEditing = editingCommentId === reply.id;
                      const replyAuthorPath = reply.authorId
                        ? getProfilePath({
                            id: reply.authorId,
                            uid: reply.authorId,
                            name: replyDisplayName,
                          })
                        : "";
                      const canOpenReplyAuthorProfile = Boolean(
                        !replyIsOfficial && reply.authorId
                      );

                      return (
                        <div
                          className={`community-comment is-reply${
                            replyIsOfficial ? " is-official-comment" : ""
                          }`}
                          key={reply.id}
                        >
                          <button
                            type="button"
                            className="community-comment-avatar community-author-link"
                            onClick={() =>
                              canOpenReplyAuthorProfile && navigate(replyAuthorPath)
                            }
                            disabled={!canOpenReplyAuthorProfile}
                            aria-label={`View ${replyDisplayName}'s profile`}
                          >
                            {replyIsOfficial ? (
                              <img src={logoBanner} alt="MyFarmingPal logo" />
                            ) : (
                              <FarmAvatar
                                profile={{
                                  id: reply.authorId,
                                  uid: reply.authorId,
                                  name: replyDisplayName,
                                  photo: reply.authorPhoto,
                                  farmAnimal: reply.authorFarmAnimal,
                                  farmAnimalSelectionExplicit:
                                    reply.authorFarmAnimalSelectionExplicit === true,
                                }}
                                className="community-comment-avatar-inner"
                              />
                            )}
                          </button>

                          <div className="community-comment-body">
                            <div className="community-comment-meta">
                              <button
                                type="button"
                                className="community-author-name-link"
                                onClick={() =>
                                  canOpenReplyAuthorProfile && navigate(replyAuthorPath)
                                }
                                disabled={!canOpenReplyAuthorProfile}
                              >
                                {replyDisplayName}
                              </button>
                              {replyIsOfficial && (
                                <span className="community-official-badge">Official</span>
                              )}
                              <span>{formatCommentDate(reply.createdAt)}</span>
                            </div>
                            {replyIsEditing ? (
                              <form
                                className="community-comment-edit-form"
                                onSubmit={handleSaveCommentEdit}
                              >
                                <input
                                  type="text"
                                  value={editBody}
                                  onChange={(event) => setEditBody(event.target.value)}
                                  maxLength={800}
                                />
                                <div className="community-comment-actions">
                                  <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={savingEdit}
                                  >
                                    {savingEdit ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-soft"
                                    onClick={() => {
                                      setEditingCommentId(null);
                                      setEditBody("");
                                    }}
                                    disabled={savingEdit}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            ) : (
                              <p>{reply.body}</p>
                            )}
                            <div className="community-comment-actions">
                              <ReportButton
                                type="community_comment"
                                targetId={reply.id}
                                targetOwnerId={reply.authorId}
                                targetTitle={`Reply on ${post.title || "community post"}`}
                                extraData={{
                                  parentPostId: post.id,
                                  parentPostTitle: post.title || "",
                                  parentCommentId: comment.id,
                                  targetCommentText: reply.body || "",
                                }}
                                compact
                              />
                              {replyCanEdit && !replyIsEditing && (
                                <button
                                  type="button"
                                  className="btn-soft"
                                  onClick={() => startEditingComment(reply)}
                                >
                                  Edit
                                </button>
                              )}
                              {replyCanRemove && (
                                <button
                                  type="button"
                                  className="btn-danger-soft"
                                  onClick={() => handleRemoveComment(reply)}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommunityComments;
